import React, { useEffect, useRef, useState } from 'react'
import { Button, Drawer, IconButton, Step, StepLabel, Stepper } from '@material-ui/core'
import { Close as CloseIcon } from '@material-ui/icons'
import {
  NM_CCD_ENVIRONMENTS,
  getNmCcdProviderErrorMessage,
  validateNmCcdForm,
} from '../../utils/nmCcdCompliance'
import {
  DIFF_FILTERS,
  MIGRATION_PRIMARY_LABELS,
  MIGRATION_STEPS,
  TASK_STATUS,
  batchCount,
  buildDiffCsv,
  diffCsvFilename,
  formatCount,
  initialSwitchOverTasks,
  migrationErrorMessage,
  saveTextFile,
  TOKEN_REQUIRED_MESSAGE,
  validatedIdentity,
} from '../../utils/nmCcdMigration'
import {
  CompareBatchesStep,
  FetchInventoryStep,
  SwitchOverStep,
  ValidateTokenStep,
} from './NmCcdMigrationSteps'

const STEP = { VALIDATE: 0, FETCH: 1, COMPARE: 2, SWITCH: 3 }
const TITLE_ID = 'nmccd-migration-title'

const initialState = () => ({
  step: STEP.VALIDATE,
  jwt: '',
  environment: '',
  validating: false,
  saving: false,
  validation: null,
  validationError: '',
  fetching: false,
  inventory: null,
  fetchError: '',
  preview: null,
  ack: false,
  query: '',
  filter: DIFF_FILTERS.ALL,
  page: 0,
  tasks: initialSwitchOverTasks(),
  running: false,
  done: false,
  failure: null,
  mapResult: null,
})

// Right-hand wizard that walks a shop from BioTrack to S2S (NM CCD). `onTest` is the row's
// connection test; `migration` carries the container callbacks: fetchInventory (live read-only
// preview, reused by the compare step), enable, syncPackages, mapBatches, disableBiotrack.
const NmCcdMigrationWizard = ({
  open,
  onClose,
  onTest,
  account = {},
  shop = {},
  migration = {},
  notifyError = () => {},
  onMigrated = () => {},
}) => {
  const [state, setState] = useState(initialState)
  const mounted = useRef(true)
  const sequence = useRef(0)
  const latest = useRef({})
  const saving = useRef(false)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (open) {
      latest.current = {}
      saving.current = false
      setState(initialState())
    }
  }, [open])

  // Without a saved token the environment is still open; the choice made here rides along on
  // every request until step 1 saves it.
  const environmentEditable = !account.nmCcdJwtTokenConfigured
  const draft =
    environmentEditable && state.environment
      ? { ...account, environment: state.environment }
      : account

  const patch = (changes) => {
    if (mounted.current) setState((current) => ({ ...current, ...changes }))
  }

  // Requests stay retryable while in flight; only the most recent response for a kind is applied.
  const begin = (kind) => {
    const id = ++sequence.current
    latest.current[kind] = id
    return () => mounted.current && latest.current[kind] === id
  }

  const validate = () => {
    const newToken = state.jwt.trim()
    if (!newToken && !account.nmCcdJwtTokenConfigured) {
      patch({ validation: null, validationError: TOKEN_REQUIRED_MESSAGE })
      return
    }
    if (saving.current) return
    const isCurrent = begin('validate')
    const fail = (message) => {
      patch({ validating: false, validation: null, validationError: message })
      notifyError(message)
    }
    patch({ validating: true, validationError: '' })
    Promise.resolve()
      .then(() => onTest(draft, state.jwt))
      .then((result) => {
        if (!isCurrent()) return null
        if (!result || result.success === false) {
          fail(getNmCcdProviderErrorMessage(result || {}))
          return null
        }
        if (account.id && !newToken) return result
        // A new account, or a new token, is saved now (enabled flags untouched) so the
        // read-only preview in the next steps can use it. One save at a time.
        const identity = validatedIdentity(result)
        const errors = validateNmCcdForm({ ...draft, ...identity }, state.jwt, null)
        if (Object.keys(errors).length) {
          fail(Object.values(errors)[0])
          return null
        }
        saving.current = true
        patch({ saving: true })
        return migration
          .saveConfiguration(draft, state.jwt, identity)
          .then(() => result)
          .finally(() => {
            saving.current = false
            patch({ saving: false })
          })
      })
      .then((result) => {
        if (!isCurrent() || !result) return
        patch({ validating: false, validation: result })
      })
      .catch((error) => {
        if (!isCurrent()) return
        fail(getNmCcdProviderErrorMessage(error))
      })
  }

  const fetchInventory = () => {
    const isCurrent = begin('fetch')
    patch({ fetching: true, fetchError: '' })
    Promise.resolve()
      .then(() => migration.fetchInventory())
      .then((preview) => {
        if (!isCurrent()) return
        patch({
          fetching: false,
          inventory: {
            s2sPackageCount: preview.s2sPackageCount,
            blazeBatchCount: preview.blazeBatchCount,
            fetchedAt: preview.fetchedAt,
          },
          preview,
          ack: false,
          page: 0,
        })
      })
      .catch((error) => {
        if (!isCurrent()) return
        patch({
          fetching: false,
          fetchError: migrationErrorMessage(error, 'The S2S inventory could not be fetched.'),
        })
      })
  }

  const runSwitchOver = async () => {
    let tasks = state.tasks.map((task) =>
      task.status === TASK_STATUS.DONE
        ? task
        : { ...task, status: TASK_STATUS.PENDING, detail: 'Waiting' },
    )
    const update = (key, changes) => {
      tasks = tasks.map((task) => (task.key === key ? { ...task, ...changes } : task))
      patch({ tasks })
    }
    patch({ running: true, failure: null, tasks })

    const runners = {
      enable: async () => {
        update('enable', {
          status: TASK_STATUS.RUNNING,
          detail: `Enabling organization ${account.nmCcdOrganizationId || ''}…`,
        })
        await migration.enable(account, state.jwt)
        update('enable', {
          status: TASK_STATUS.RUNNING,
          detail: 'Enabled. Syncing S2S inventory into Blaze…',
        })
        await migration.syncPackages()
        update('enable', {
          status: TASK_STATUS.DONE,
          detail: 'Enabled with sync on. S2S inventory synced.',
        })
      },
      map: async () => {
        const matched = state.preview ? state.preview.matchedCount : 0
        update('map', {
          status: TASK_STATUS.RUNNING,
          detail: `Moving ${batchCount(matched)} to S2S…`,
        })
        const result = (await migration.mapBatches()) || {}
        const mapResult = {
          mappedCount: Number(result.mappedCount || 0),
          skippedCount: Number(result.skippedCount || 0),
        }
        patch({ mapResult })
        if (matched > 0 && mapResult.mappedCount === 0) {
          throw {
            message:
              'No batches were mapped. The S2S inventory sync may not have completed; check Compliance > Packages, then click Try again.',
          }
        }
        update('map', {
          status: TASK_STATUS.DONE,
          detail: `${batchCount(mapResult.mappedCount)} now on S2S${
            mapResult.skippedCount ? ` · ${formatCount(mapResult.skippedCount)} skipped` : ''
          }`,
        })
      },
      disable: async () => {
        update('disable', {
          status: TASK_STATUS.RUNNING,
          detail: `Turning off BioTrack for ${shop.name || 'this shop'}…`,
        })
        const result = (await migration.disableBiotrack()) || {}
        update('disable', {
          status: TASK_STATUS.DONE,
          detail:
            result.disabled === false
              ? 'No BioTrack location to disable.'
              : 'BioTrack disabled. Delivery and sync stopped.',
        })
      },
    }

    for (const task of tasks) {
      if (task.status === TASK_STATUS.DONE) continue
      try {
        await runners[task.key]()
      } catch (error) {
        const message = migrationErrorMessage(error, 'The request failed.')
        update(task.key, { status: TASK_STATUS.FAILED, detail: message })
        patch({ running: false, failure: { message } })
        return
      }
    }
    patch({ running: false, done: true })
    if (mounted.current) onMigrated()
  }

  const downloadCsv = (rows) =>
    saveTextFile(buildDiffCsv(rows), diffCsvFilename(state.preview && state.preview.truncated))

  const started = state.running || state.done || Boolean(state.failure)
  const needsAck = Boolean(state.preview && state.preview.unmatchedCount > 0 && !state.ack)
  const primaryEnabled = [
    Boolean(state.validation) && !state.validating,
    Boolean(state.inventory) && !state.fetching,
    Boolean(state.preview) && !needsAck,
    Boolean(state.preview),
  ][state.step]
  const primaryLabel = state.failure ? 'Try again' : MIGRATION_PRIMARY_LABELS[state.step]
  const primaryAction = () =>
    state.step === STEP.SWITCH ? runSwitchOver() : patch({ step: state.step + 1 })
  const footerNote =
    state.step === STEP.SWITCH && !started ? 'Confirm to apply all three changes.' : ''

  const handleClose = () => {
    if (state.running) return
    onClose({ refresh: state.done || state.tasks.some((task) => task.status === TASK_STATUS.DONE) })
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      classes={{ paper: 'nmccd-migration__paper' }}
      ModalProps={{ 'aria-labelledby': TITLE_ID }}
    >
      <div className="nmccd-migration">
        <div className="nmccd-migration__header">
          <div className="nmccd-migration__title-row">
            <div>
              <div className="nmccd-migration__title" id={TITLE_ID}>
                Migrate BioTrack to S2S
              </div>
              <div className="nmccd-migration__subtitle">
                {shop.name || ''} · NM CCD · {draft.environment || ''}
                {account.nmCcdLocationId ? ` · Location ${account.nmCcdLocationId}` : ''}
              </div>
            </div>
            <IconButton
              className="nmccd-migration__close"
              onClick={handleClose}
              disabled={state.running}
              aria-label="Close migration wizard"
            >
              <CloseIcon />
            </IconButton>
          </div>
          <Stepper activeStep={state.step} classes={{ root: 'nmccd-migration__stepper' }}>
            {MIGRATION_STEPS.map((label) => (
              <Step key={label}>
                <StepLabel
                  classes={{ label: 'nmccd-migration__step-label' }}
                  StepIconProps={{
                    classes: {
                      active: 'nmccd-migration__step-icon',
                      completed: 'nmccd-migration__step-icon',
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </div>

        <div className="nmccd-migration__body">
          {state.step === STEP.VALIDATE ? (
            <ValidateTokenStep
              account={draft}
              environmentEditable={environmentEditable}
              environments={NM_CCD_ENVIRONMENTS}
              onEnvironmentChange={(environment) =>
                patch({ environment, validation: null, validationError: '' })
              }
              jwt={state.jwt}
              onJwtChange={(jwt) => patch({ jwt, validation: null, validationError: '' })}
              validating={state.validating}
              saving={state.saving}
              result={state.validation}
              error={state.validationError}
              onValidate={validate}
            />
          ) : null}
          {state.step === STEP.FETCH ? (
            <FetchInventoryStep
              account={account}
              fetching={state.fetching}
              inventory={state.inventory}
              error={state.fetchError}
              onFetch={fetchInventory}
            />
          ) : null}
          {state.step === STEP.COMPARE ? (
            <CompareBatchesStep
              preview={state.preview}
              ack={state.ack}
              onAckChange={(ack) => patch({ ack })}
              query={state.query}
              onQueryChange={(query) => patch({ query, page: 0 })}
              filter={state.filter}
              onFilterChange={(filter) => patch({ filter, page: 0 })}
              page={state.page}
              onPageChange={(page) => patch({ page })}
              onDownloadCsv={downloadCsv}
            />
          ) : null}
          {state.step === STEP.SWITCH ? (
            <SwitchOverStep
              shop={shop}
              account={account}
              preview={state.preview}
              tasks={state.tasks}
              running={state.running}
              done={state.done}
              failure={state.failure}
              mapResult={state.mapResult}
            />
          ) : null}
        </div>

        <div className="nmccd-migration__footer">
          {state.step > STEP.VALIDATE && !started ? (
            <Button
              className="nmccd-migration__text-button"
              onClick={() => patch({ step: state.step - 1 })}
            >
              Back
            </Button>
          ) : null}
          <div className="nmccd-migration__footer-right">
            {footerNote ? <span className="nmccd-migration__footer-note">{footerNote}</span> : null}
            {!state.running ? (
              <Button className="nmccd-migration__text-button" onClick={handleClose}>
                {state.done ? 'Close' : 'Cancel'}
              </Button>
            ) : null}
            {!state.running && !state.done ? (
              <Button className="primary-button" disabled={!primaryEnabled} onClick={primaryAction}>
                {primaryLabel}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </Drawer>
  )
}

export default NmCcdMigrationWizard



// WEBPACK FOOTER //
// ./src/components/Compliance/NmCcdMigrationWizard.js